<template>
<div>
    <!-- Hero Section -->
    <section class="pt-12">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-5 col-lg-6 order-md-2">
                    <img src="/assets/img/illustrations/illustration-7.png" class="img-fluid mw-md-150 mw-lg-130 mb-6 mb-md-0" alt="...">
                </div>
                <div class="col-12 col-md-7 col-lg-6 order-md-1">
                    <h1 class="display-5 text-center text-md-start">
                        <span class="text-primary text-nowrap">
                            Welcome to DataLikers!
                        </span>
                    </h1>
                    <p class="lead text-center text-md-start text-muted mb-2">
                        Unlock Social Media Data at Scale
                    </p>
                    <!-- <p class="fs-lg text-primary-desat text-center mb-2"> -->
                    <!--     No rate limits. No blocks. No scraping headaches. -->
                    <!-- </p> -->
                    <p class="fs-lg text-center text-md-start text-muted mb-2">
                        Get instant access to millions of Instagram and TikTok profiles, posts, followers, and comments through our fast API, AI-powered MCP, or ready-made datasets.
                    </p>
                    <p class="text-success text-center text-md-start fw-bold mb-4" v-show="!$state.context.id">
                        <i class="fe fe-gift me-2"></i>{{ $state.context.free_requests }} free requests trial after Telegram verification
                    </p>
                    <p v-if="reviewAgg.count" class="text-center text-md-start small mb-4">
                        <router-link to="/reviews" class="text-decoration-none d-inline-flex align-items-center gap-2 text-muted">
                            <span style="color:#ffc107;letter-spacing:.05em;font-size:1rem;">★★★★★</span>
                            <span><strong class="text-white">{{ reviewAgg.average }}</strong> / 5 from {{ reviewAgg.count }} customer reviews →</span>
                        </router-link>
                    </p>
                    <div class="text-center text-md-start mb-6 mb-md-0" v-show="!$state.context.id">
                        <router-link :to="{path: '/login'}" class="btn btn-primary shadow lift me-1">
                            Login <i class="fe fe-arrow-right d-none d-md-inline ms-3"></i>
                        </router-link>
                        <router-link :to="{path: '/registration'}" class="btn btn-primary-soft lift">
                            Registration
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Services Section -->
    <section class="py-8 py-md-11 bg-gray-200">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 class="fw-bold">Our Products</h2>
                    <p class="fs-lg text-muted mb-7">
                        Choose the best way to access social media data
                    </p>
                </div>
            </div>
            <div class="row">
                <!-- Cache API -->
                <div class="col-12 col-md-4 mb-4">
                    <div class="card shadow-light-lg h-100">
                        <div class="card-body">
                            <div class="icon text-primary mb-3">
                                <i class="fe fe-zap" style="font-size: 2.5rem;"></i>
                            </div>
                            <h4 class="fw-bold">Cache API</h4>
                            <p class="text-muted mb-2">
                                Fast REST API to query our cache of Instagram data. Get user profiles, followers, posts, and comments instantly.
                            </p>
                            <p class="text-primary fw-bold mb-4">
                                From $0.0003 per request
                            </p>
                            <router-link :to="{path: '/cache-api'}" class="btn btn-sm btn-primary-soft">
                                Learn More <i class="fe fe-arrow-right ms-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
                <!-- MCP -->
                <div class="col-12 col-md-4 mb-4">
                    <div class="card shadow-light-lg h-100">
                        <div class="card-body">
                            <div class="icon text-primary mb-3">
                                <i class="fe fe-cpu" style="font-size: 2.5rem;"></i>
                            </div>
                            <h4 class="fw-bold">MCP Server</h4>
                            <p class="text-muted mb-2">
                                Connect AI assistants like Claude directly to our data using Model Context Protocol. Ask questions in natural language.
                            </p>
                            <p class="text-primary fw-bold mb-4">
                                Same pricing as Cache API
                            </p>
                            <router-link :to="{path: '/mcp'}" class="btn btn-sm btn-primary-soft">
                                Setup MCP <i class="fe fe-arrow-right ms-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
                <!-- Datasets -->
                <div class="col-12 col-md-4 mb-4">
                    <div class="card shadow-light-lg h-100">
                        <div class="card-body">
                            <div class="icon text-primary mb-3">
                                <i class="fe fe-database" style="font-size: 2.5rem;"></i>
                            </div>
                            <h4 class="fw-bold">Datasets</h4>
                            <p class="text-muted mb-2">
                                Download bulk data exports from our cache. Perfect for research, analytics, and machine learning projects.
                            </p>
                            <p class="text-primary fw-bold mb-4">
                                Fixed price per dataset
                            </p>
                            <router-link :to="{path: '/datasets'}" class="btn btn-sm btn-primary-soft">
                                Browse Datasets <i class="fe fe-arrow-right ms-2"></i>
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Datasets Section -->
    <section v-if="products.length > 0" class="py-8 py-md-11">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 class="fw-bold">Available Datasets</h2>
                    <p class="fs-lg text-muted mb-7">
                        Ready-made data collections from Instagram and TikTok
                    </p>
                </div>
            </div>
            <div class="row justify-content-center">
                <div class="col-12">
                    <ProductsTable :products="products" />
                </div>
            </div>
        </div>
    </section>

    <!-- API Pricing Section -->
    <section class="py-8 py-md-11 bg-gray-200">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 class="fw-bold">Cache API & MCP Pricing</h2>
                    <p class="fs-lg text-muted mb-7">
                        Pay only for what you use. Each <router-link to="/cache-api">Cache API</router-link> request or <router-link to="/mcp">MCP</router-link> tool call is charged based on your plan.
                    </p>
                </div>
            </div>
            <Plans button="Choose Plan" :currentPlanId="$state.context.plan?.id" />
        </div>
    </section>

    <!-- Code Examples Section -->
    <section class="py-8 py-md-11">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 class="fw-bold">Easy Integration</h2>
                    <p class="fs-lg text-muted mb-7">
                        Get started with our Cache API in your favorite programming language
                    </p>
                </div>
            </div>
            <CodeExamples />
        </div>
    </section>

    <!-- CTA Section -->
    <section class="py-8 py-md-11 bg-gray-200">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-12 col-md-10 col-lg-8 text-center">
                    <h2 v-show="!$state.context.id">
                        <router-link class="fw-bold text-primary-desat" :to="{path: '/registration'}">
                            Register
                        </router-link> and start using our service!
                    </h2>
                    <p class="fs-lg text-muted mb-7 mb-md-9">
                        Top up your account in your personal dashboard, get token and use any plan
                    </p>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
import Plans from './Plans.vue'
import CodeExamples from './CodeExamples.vue'
import ProductsTable from './ProductsTable.vue'

export default {
    components: { Plans, CodeExamples, ProductsTable },
    data() {
        return {
            products: [],
            reviewAgg: { count: 0, average: 0 },
        }
    },
    computed: {
        prefix() {
            return `https://api.${location.hostname}`
        }
    },
    methods: {
        fetchProducts() {
            this.$services.Products().get().then(resp => {
                if (resp.body.state) {
                    this.products = resp.body.products
                }
            })
        }
    },
    created() {
        if (this.$route.params && this.$route.params.promocode)
            localStorage.setItem("promocode", this.$route.params.promocode);
        if (document.referrer)
            localStorage.setItem("referrer", document.referrer);
        if (location.search.indexOf("paymentId") != -1) {
            this.$router.replace({path: '/billing'})
        }
        this.fetchProducts()
        // Visible AggregateRating must agree with the SoftwareApplication
        // JSON-LD on / (lm pages.py).
        this.$services.ReviewAggregate().get().then(r => {
            this.reviewAgg = r.body
        }).catch(() => {})
    }
}
</script>
